import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Phone, Tablet, Laptop, ChevronDown, Check, type LucideIcon } from 'lucide-react';
import { workbenchStore } from '~/lib/stores/workbench';
import { ElementStylePanel } from './ElementStylePanel';
import { expoUrlAtom } from '~/lib/stores/qrCodeStore';
import { ExpoQrModal } from '~/components/workbench/ExpoQrModal';
import { classNames } from '~/utils/classNames';
import { chatStore } from '~/lib/stores/chat';
import { chatId } from '~/lib/persistence/useChatHistory';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { capture } from '~/lib/posthog';
import { streamingState } from '~/lib/stores/streaming';
import { $activeGeneration } from '~/lib/stores/activeGeneration';
import { SharePreviewButton } from '~/components/workbench/SharePreviewButton';
import { runtime } from '~/lib/runtime';
import { $wakeUpFn } from '~/lib/stores/sandbox';
import httpClient from '~/lib/http-client';
import {
  $elementEditFallback,
  $selectedElement,
  $selectMode,
  $selectorAvailable,
  $styleDeltas,
  clearSelection,
  setSelectedElement,
  type SelectedElementPayload,
  type StyleDeltas,
} from '~/lib/stores/element-selector';
import { toast } from 'react-toastify';
import { applyImageSwapDirect, applyStyleEditDirect, applyTextEditDirect } from '~/lib/element-selector/direct-edit';
import { trackElementSelected, trackVisualEdit } from '~/lib/element-selector/analytics';
import { buildImageSwapFallbackPrompt, buildStyleEditPrompt } from '~/lib/element-selector/markers';
import { apiUploadStorageFile } from '~/lib/api/storage';
import {
  appendReloadParam,
  pickReloadUrl,
  fetchPreviewUrlWithRetry,
  previewOrigin,
  getVersionPreviewUrl,
  isPreviewReachable,
} from './preview-url';
import { VersionSelector } from './VersionSelector';
import { $peekVersion, exitPeek, requestRestore } from '~/lib/stores/version-peek';
import { $previewNavigation, previewNavigationUrl } from '~/lib/stores/preview-navigation';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import { $canvasMode, enterCanvasMode } from '~/lib/stores/canvas';
import { CanvasMode } from '~/components/canvas/CanvasMode';
import { Tooltip } from '~/components/ui/Tooltip';
import { BREAKPOINTS, useIsBelow, useIsTouch } from '~/lib/hooks/useMediaQuery';

// Toolbar labels: shown on the full-width overlay (md–lg) and wide desktops (xl+);
// icon-only below md and in the 1024–1279px split, where they would clip.
const TOOLBAR_LABEL = 'hidden md:inline lg:hidden xl:inline';

function sanitizeUrlForHtml(url: string): string {
  try {
    const parsed = new URL(url);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '';
    }

    return url.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  } catch {
    return '';
  }
}

// The iframe content is agent/user-generated, so a forged DUNEA_ELEMENT_SELECTED
// could send oversized strings/objects to bloat the prompt + persisted message.
// Bound every field at the trust boundary (curated computedStyles has 14 keys).
const isValidSelectionPayload = (p: unknown): p is SelectedElementPayload => {
  const s = p as SelectedElementPayload;
  return (
    !!p &&
    typeof s.file === 'string' &&
    s.file.length <= 1024 &&
    typeof s.line === 'number' &&
    typeof s.tag === 'string' &&
    s.tag.length <= 64 &&
    typeof s.classes === 'string' &&
    s.classes.length <= 2048 &&
    typeof s.textSnippet === 'string' &&
    s.textSnippet.length <= 2048 &&
    (s.text === undefined || (typeof s.text === 'string' && s.text.length <= 2048)) &&
    (s.src === undefined || (typeof s.src === 'string' && s.src.length <= 2048)) &&
    (s.editableText === undefined || typeof s.editableText === 'boolean') &&
    typeof s.computedStyles === 'object' &&
    s.computedStyles !== null &&
    !Array.isArray(s.computedStyles) &&
    Object.keys(s.computedStyles).length <= 32
  );
};

type ResizeSide = 'left' | 'right' | null;

interface PreviewProps {
  minimal?: boolean;
}

type DeviceCategory = 'phone' | 'tablet' | 'desktop';

interface WindowSize {
  name: string;
  width: number;
  height: number;
  icon: string;
  hasFrame?: boolean;
  frameType?: 'mobile' | 'tablet' | 'laptop' | 'desktop';
  /** Grouping bucket for the categorized device picker. */
  category: DeviceCategory;
  /** Human-friendly descriptor shown instead of raw dimensions. */
  label: string;
}

const WINDOW_SIZES: WindowSize[] = [
  {
    name: 'iPhone SE',
    width: 375,
    height: 667,
    icon: 'i-ph:device-mobile',
    hasFrame: true,
    frameType: 'mobile',
    category: 'phone',
    label: 'Small phone',
  },
  {
    name: 'iPhone 12/13',
    width: 390,
    height: 844,
    icon: 'i-ph:device-mobile',
    hasFrame: true,
    frameType: 'mobile',
    category: 'phone',
    label: 'Standard phone',
  },
  {
    name: 'iPhone 12/13 Pro Max',
    width: 428,
    height: 926,
    icon: 'i-ph:device-mobile',
    hasFrame: true,
    frameType: 'mobile',
    category: 'phone',
    label: 'Large phone',
  },
  {
    name: 'iPad Mini',
    width: 768,
    height: 1024,
    icon: 'i-ph:device-tablet',
    hasFrame: true,
    frameType: 'tablet',
    category: 'tablet',
    label: 'Small tablet',
  },
  {
    name: 'iPad Air',
    width: 820,
    height: 1180,
    icon: 'i-ph:device-tablet',
    hasFrame: true,
    frameType: 'tablet',
    category: 'tablet',
    label: 'Tablet',
  },
  {
    name: 'iPad Pro 11"',
    width: 834,
    height: 1194,
    icon: 'i-ph:device-tablet',
    hasFrame: true,
    frameType: 'tablet',
    category: 'tablet',
    label: 'Large tablet',
  },
  {
    name: 'iPad Pro 12.9"',
    width: 1024,
    height: 1366,
    icon: 'i-ph:device-tablet',
    hasFrame: true,
    frameType: 'tablet',
    category: 'tablet',
    label: 'Pro tablet',
  },
  {
    name: 'Small Laptop',
    width: 1280,
    height: 800,
    icon: 'i-ph:laptop',
    hasFrame: true,
    frameType: 'laptop',
    category: 'desktop',
    label: 'Laptop',
  },
  {
    name: 'Laptop',
    width: 1366,
    height: 768,
    icon: 'i-ph:laptop',
    hasFrame: true,
    frameType: 'laptop',
    category: 'desktop',
    label: 'Laptop',
  },
  {
    name: 'Large Laptop',
    width: 1440,
    height: 900,
    icon: 'i-ph:laptop',
    hasFrame: true,
    frameType: 'laptop',
    category: 'desktop',
    label: 'Large laptop',
  },
  {
    name: 'Desktop',
    width: 1920,
    height: 1080,
    icon: 'i-ph:monitor',
    hasFrame: true,
    frameType: 'desktop',
    category: 'desktop',
    label: 'Desktop',
  },
  {
    name: '4K Display',
    width: 3840,
    height: 2160,
    icon: 'i-ph:monitor',
    hasFrame: true,
    frameType: 'desktop',
    category: 'desktop',
    label: '4K display',
  },
];

interface DeviceCategoryMeta {
  key: DeviceCategory;
  title: string;
  icon: LucideIcon;
}

const DEVICE_CATEGORIES: DeviceCategoryMeta[] = [
  { key: 'phone', title: 'Phones', icon: Phone },
  { key: 'tablet', title: 'Tablets', icon: Tablet },
  { key: 'desktop', title: 'Desktop', icon: Laptop },
];

// Rotating educational tips shown in the mono ticker while the sandbox boots.
const LOADING_TIPS = [
  'Built with React + Vite + Tailwind CSS',
  'Your app runs in an isolated Kubernetes pod',
  'Preview updates live as the AI writes code',
  'shadcn/ui components available via component library',
  'The AI can run shell commands and install packages',
  'AI reads your code before making changes',
  'Warm pods mean near-instant sandbox startup',
];

/*
 * Preview idle / boot state. Shown while the sandbox spins up or the iframe
 * hasn't painted yet. Rather than a bare spinner on flat black, it renders the
 * DESIGN.md atmosphere — a radially-masked 64px hairline grid + the signature
 * radial lime glow behind a slightly-tilted browser-frame skeleton — so the
 * panel previews the *act of building* instead of stalling. An editorial serif
 * line sits above a mono ticker of educational tips and a thin progress crawl.
 * All motion is gated behind useReducedMotion.
 */
export const PreviewLoadingCarousel = memo(() => {
  const [tip, setTip] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    const interval = setInterval(() => {
      setTip((prev) => (prev + 1) % LOADING_TIPS.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-9 bg-page overflow-hidden">
      {/* hairline 64px grid, radially masked (DESIGN.md §3) */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.65] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(120% 90% at 50% 42%, #000 0%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(120% 90% at 50% 42%, #000 0%, transparent 72%)',
        }}
      />
      {/* signature radial lime glow, pooled under the frame (DESIGN.md §3) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 52% 46% at 50% 45%, rgba(217,242,106,0.16) 0%, transparent 64%)',
        }}
      />

      {/* tilted browser-frame skeleton — the site "taking shape" */}
      <div
        aria-hidden
        className="relative rounded-[13px] border border-subtle bg-surface overflow-hidden"
        style={{
          width: 'min(520px, 78%)',
          boxShadow: '0 40px 90px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.02)',
          transform: reduce ? 'none' : 'perspective(1400px) rotateX(2.5deg)',
          transformOrigin: 'center 70%',
        }}
      >
        {/* browser chrome */}
        <div className="flex items-center gap-1.75 px-3.25 py-2.5 border-b border-hairline bg-raised">
          <span className="w-2.25 h-2.25 rounded-full bg-elevated" />
          <span className="w-2.25 h-2.25 rounded-full bg-elevated" />
          <span className="w-2.25 h-2.25 rounded-full bg-elevated" />
          <span className="ml-2 flex-1 h-4 rounded-[5px] bg-sunken" />
        </div>
        {/* skeleton page body with a single sweeping shimmer */}
        <div className="relative flex flex-col gap-3.5 p-5 h-[210px] overflow-hidden">
          <div className="h-3 w-[38%] rounded-md bg-raised" />
          <div className="h-6 w-[74%] rounded-md bg-sunken" />
          <div className="h-6 w-[52%] rounded-md bg-sunken" />
          <div className="h-2.5 w-[88%] rounded bg-raised" />
          <div className="h-2.5 w-[66%] rounded bg-raised" />
          <div className="flex gap-3 mt-1.5">
            <div className="flex-1 h-14 rounded-lg bg-raised" />
            <div className="flex-1 h-14 rounded-lg bg-raised" />
            <div className="flex-1 h-14 rounded-lg bg-raised" />
          </div>
          {!reduce && (
            <motion.div
              className="absolute inset-y-0 w-1/3 pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)' }}
              initial={{ x: '-120%' }}
              animate={{ x: '340%' }}
              transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
        </div>
      </div>

      {/* editorial line + mono ticker + progress crawl */}
      <div className="relative flex flex-col items-center gap-4 text-center max-w-[420px] px-6">
        <div className="ui-serif text-primary" style={{ fontSize: 27, lineHeight: 1.15 }}>
          Setting the <span className="italic text-accent">stage.</span>
        </div>

        <div className="h-5 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={tip}
              className="ui-mono text-[11.5px] text-muted"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
            >
              {LOADING_TIPS[tip]}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="w-[240px] h-[3px] rounded-full bg-sunken overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-accent"
            style={{ boxShadow: '0 0 10px var(--accent-glow)' }}
            initial={{ width: '14%' }}
            animate={reduce ? { width: '44%' } : { width: ['14%', '66%', '30%', '80%', '14%'] }}
            transition={reduce ? undefined : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </div>
    </div>
  );
});

const StaticPreviewBanner = memo(() => {
  const wakeUpFn = useStore($wakeUpFn);
  const [waking, setWaking] = useState(false);

  const handleResume = async () => {
    if (waking) {
      return;
    }

    if (!wakeUpFn) {
      // Mirrors SleepBanner: the session-timeout path reaches 'sleeping' with no
      // closure while hasStaticPreview stays true, so this banner rendered a
      // Resume button that did nothing at all. Reload rather than pretend.
      console.warn('[StaticPreviewBanner] No wake closure registered — reloading to recover');
      window.location.reload();

      return;
    }

    setWaking(true);

    // Snapshot before clearing: clearing shows the loading carousel, but it
    // also unmounts this banner (isStaticPreview goes false), so on failure
    // both must be put back or the static preview and its Resume button are
    // gone for the rest of the session.
    const hadStaticPreview = runtime.hasStaticPreview.get();
    const previousPreviews = workbenchStore.previews.get();

    runtime.hasStaticPreview.set(false);
    workbenchStore.previews.set([]);

    try {
      await wakeUpFn();
    } catch (err) {
      // Same as SleepBanner: keep the closure, this button IS the retry. And
      // reset `waking` on every path — the success branch used to leave it
      // stuck true, so if the resume resolved without a preview this banner sat
      // on a permanent spinner.
      console.error('Resume failed:', err);
      runtime.hasStaticPreview.set(hadStaticPreview);
      workbenchStore.previews.set(previousPreviews);
      toast.error(err instanceof Error ? err.message : 'Resume failed — please try again.');
    } finally {
      setWaking(false);
    }
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center gap-3 px-4 py-3 bg-raised/95 backdrop-blur-md border-t border-hairline">
      <div className="flex-shrink-0 w-7 h-7 rounded-md bg-elevated flex items-center justify-center">
        <span className="i-ph:monitor text-sm text-muted" />
      </div>
      <p className="flex-1 text-xs text-accent min-w-0">
        You're viewing a static preview. Resume to interact with the app.
      </p>
      <button
        onClick={handleResume}
        disabled={waking}
        className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer disabled:cursor-wait bg-white text-page hover:bg-white/90"
      >
        {waking ? (
          <>
            <span className="i-svg-spinners:90-ring-with-bg text-xs" />
            Resuming...
          </>
        ) : (
          'Resume Preview'
        )}
      </button>
    </div>
  );
});

StaticPreviewBanner.displayName = 'StaticPreviewBanner';

interface DeviceCardProps {
  size: WindowSize;
  selected: boolean;
  landscape: boolean;
  framed: boolean;
  onSelect: () => void;
}

/**
 * A single selectable device card in the Preview Settings panel. Reads as a real
 * card, not a list row: a resting surface a touch lighter than the panel, a soft
 * hairline border, and on hover a 1px lift with an intentional soft shadow — no
 * scale, no glow. Active gets a soft lime border, a quiet lime tint, and a right
 * checkmark. The secondary line shows a human-friendly label ("Standard phone")
 * and cross-fades to the exact resolution on hover.
 */
const DeviceCard = memo(({ size, selected, landscape, framed, onSelect }: DeviceCardProps) => {
  const rotated = landscape && (size.frameType === 'mobile' || size.frameType === 'tablet');
  const resolution = rotated ? `${size.height} × ${size.width}` : `${size.width} × ${size.height}`;
  const framedNote = size.hasFrame && framed ? ' · framed' : '';

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={`${resolution}${framedNote}`}
      className={classNames(
        'group w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left border',
        'transition-[transform,box-shadow,background-color,border-color] duration-[180ms] ease-out',
        'will-change-transform',
        selected
          ? 'border-accent/40 bg-accent/[0.06] shadow-[0_2px_10px_-4px_rgba(0,0,0,0.35)]'
          : classNames(
              'border-hairline bg-white/[0.025]',
              'hover:-translate-y-px hover:bg-white/[0.055] hover:border-white/[0.09]',
              'hover:shadow-[0_6px_18px_rgba(0,0,0,0.18)]',
            ),
      )}
    >
      <div className="flex-1 min-w-0">
        <div
          className={classNames(
            'text-[15px] font-medium leading-tight truncate transition-colors',
            selected ? 'text-primary' : 'text-secondary group-hover:text-primary',
          )}
        >
          {size.name}
        </div>
        {/* Resting: friendly label. Hover: exact resolution, cross-faded in place. */}
        <div className="relative h-[15px] text-[12px] leading-[15px] text-muted">
          <span className="absolute inset-0 truncate transition-opacity duration-150 group-hover:opacity-0">
            {size.label}
          </span>
          <span className="absolute inset-0 truncate opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            {resolution}
            {framedNote}
          </span>
        </div>
      </div>
      {selected && <Check size={15} strokeWidth={2.5} className="text-accent flex-shrink-0" />}
    </button>
  );
});

DeviceCard.displayName = 'DeviceCard';

interface DeviceCategorySectionProps {
  meta: DeviceCategoryMeta;
  devices: WindowSize[];
  expanded: boolean;
  onToggle: () => void;
  selectedName: string;
  landscape: boolean;
  framed: boolean;
  onSelect: (size: WindowSize) => void;
}

/**
 * One collapsible device category (Phones / Tablets / Desktop). The header is a
 * premium card in its own right — hairline border on a soft dark surface, a 1px
 * lift + intentional soft shadow on hover, and a quiet lime border + faint
 * brightening when open. Single-open accordion driven by the parent. The body
 * animates height + opacity together (~200ms) so devices ease in like macOS /
 * Linear, and only scrolls when its own cards overflow — keeping the panel
 * compact.
 */
const DeviceCategorySection = memo(
  ({ meta, devices, expanded, onToggle, selectedName, landscape, framed, onSelect }: DeviceCategorySectionProps) => {
    const Icon = meta.icon;

    return (
      <div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className={classNames(
            'group w-full flex items-center gap-2.5 px-4 py-3 rounded-xl text-left border',
            'transition-[transform,box-shadow,background-color,border-color] duration-[180ms] ease-out',
            'will-change-transform',
            expanded
              ? 'border-accent/25 bg-white/[0.04] shadow-[0_2px_12px_-4px_rgba(0,0,0,0.4)]'
              : classNames(
                  'border-hairline bg-white/[0.015]',
                  'hover:-translate-y-px hover:bg-white/[0.035] hover:border-white/[0.09]',
                  'hover:shadow-[0_6px_18px_rgba(0,0,0,0.18)]',
                ),
          )}
        >
          <Icon
            size={16}
            className={classNames(
              'flex-shrink-0 transition-colors',
              expanded ? 'text-secondary' : 'text-muted group-hover:text-secondary',
            )}
          />
          <span
            className={classNames(
              'flex-1 text-[11px] font-medium uppercase tracking-wide transition-colors',
              expanded ? 'text-secondary' : 'text-muted group-hover:text-secondary',
            )}
          >
            {meta.title}
          </span>
          <ChevronDown
            size={14}
            className={classNames(
              'flex-shrink-0 transition-transform duration-200 ease-out',
              expanded ? 'rotate-0 text-secondary' : '-rotate-90 text-muted',
            )}
          />
        </button>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="flex flex-col gap-2 pt-2 max-h-[280px] overflow-y-auto">
                {devices.map((size) => (
                  <DeviceCard
                    key={size.name}
                    size={size}
                    selected={selectedName === size.name}
                    landscape={landscape}
                    framed={framed}
                    onSelect={() => onSelect(size)}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

DeviceCategorySection.displayName = 'DeviceCategorySection';

export const Preview = memo(({ minimal }: PreviewProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const { showChat } = useStore(chatStore);
  const isFullscreen = !showChat;
  const currentChatId = useStore(chatId);
  const hasSelectedPreview = useRef(false);
  const previews = useStore(workbenchStore.previews);
  const activePreview = previews[activePreviewIndex];
  const sandboxStatus = useStore(runtime.status);
  const hasStaticPreview = useStore(runtime.hasStaticPreview);
  const isSleeping = sandboxStatus === 'sleeping';
  const isStaticPreview = isSleeping && hasStaticPreview;
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  // True while THIS chat's app renders real, working content. The generated
  // router posts `{type:'dunea:preview-state', state:'ready'|'empty'|'error'}` —
  // `ready` the instant the first page route renders (even mid-build), `error`
  // when a page/layout throws or an HMR update fails. While a build is in
  // flight the loading carousel stays up over anything that isn't `ready`, so
  // the "Start prompting." stub, the "This page hit an error." stub, and Vite's
  // red overlay never flash mid-build; a later `error` re-covers until the
  // build recovers. Reset per chat below.
  const [previewHasContent, setPreviewHasContent] = useState(false);
  // True while the live preview's last load hit a host that didn't answer
  // (connection refused/reset) — the iframe is showing the browser's error
  // page, so keep the carousel over it while we retry. See handleIframeLoad.
  const [previewUnreachable, setPreviewUnreachable] = useState(false);
  const peekVersion = useStore($peekVersion);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [isDeviceModeOn, setIsDeviceModeOn] = useState(false);
  const [widthPercent, setWidthPercent] = useState<number>(37.5);
  const [currentWidth, setCurrentWidth] = useState<number>(0);
  // Below lg the workbench is already a full-screen overlay; below md there's no
  // room for device frames or the canvas editor (and the phone is the device).
  const isCompact = useIsBelow(BREAKPOINTS.lg);
  const isNarrow = useIsBelow(BREAKPOINTS.md);
  // Mobile browsers ignore window.open size features — device-sized windows are mouse-only.
  const isTouch = useIsTouch();
  const deviceWrapperRef = useRef<HTMLDivElement>(null);
  const [deviceScale, setDeviceScale] = useState(1);

  const resizingState = useRef({
    isResizing: false,
    side: null as ResizeSide,
    startX: 0,
    startWidthPercent: 37.5,
    windowWidth: window.innerWidth,
    pointerId: null as number | null,
  });

  // Reduce scaling factor to make resizing less sensitive
  const SCALING_FACTOR = 1;

  const [isWindowSizeDropdownOpen, setIsWindowSizeDropdownOpen] = useState(false);
  const [selectedWindowSize, setSelectedWindowSize] = useState<WindowSize>(WINDOW_SIZES[0]);
  // Single-open accordion for the device picker; defaults to the selected
  // device's category so the current choice is visible when the panel opens.
  // `null` = every category collapsed (clicking an open header closes it).
  const [expandedDeviceCategory, setExpandedDeviceCategory] = useState<DeviceCategory | null>(WINDOW_SIZES[0].category);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showDeviceFrame, setShowDeviceFrame] = useState(true);
  const [showDeviceFrameInPreview, setShowDeviceFrameInPreview] = useState(false);
  const expoUrl = useStore(expoUrlAtom);
  const [isExpoQrModalOpen, setIsExpoQrModalOpen] = useState(false);

  // Read-only peek at an older checkpoint's static capture. Overrides the
  // live iframe src below without disturbing any of the live preview state.
  const peekUrl = peekVersion && currentChatId ? getVersionPreviewUrl(currentChatId, peekVersion) : null;

  useEffect(() => {
    if (!activePreview) {
      setIframeUrl(undefined);
      setIframeLoaded(false);

      return undefined;
    }

    const { baseUrl } = activePreview;
    setIframeLoaded(false);

    // Static preview: load immediately, no polling needed
    if (isStaticPreview) {
      setIframeUrl(baseUrl);
      setIframeLoaded(true);

      return undefined;
    }

    // Don't set iframe URL yet — wait for dev server to respond
    setIframeUrl(undefined);

    // Poll until the dev server is actually responding. Bounded with backoff:
    // each preview-url call can now trigger a server-side dev-server recovery,
    // so an unbounded fixed-interval loop against a permanently broken pod
    // would keep requesting recoveries forever.
    let cancelled = false;
    const poll = async () => {
      // Use the preview-url API as a health check — it only succeeds when the
      // pod is running. Prefer the URL the server just returned: if the pod was
      // recreated (e.g. snapshot restore), the port-forward moved and the
      // store's baseUrl points at a dead port.
      // preview-url succeeding only proves the pod exists — also require the
      // preview host to answer, or the iframe opens on the browser's error page.
      const freshUrl = await fetchPreviewUrlWithRetry(
        async () => {
          const url = (await httpClient.get(`/api/sandbox/${currentChatId}/preview-url`)).data?.url || baseUrl;

          if (!(await isPreviewReachable(url))) {
            throw new Error('preview host not answering yet');
          }

          return url;
        },
        { attempts: 40, initialDelayMs: 1500, maxDelayMs: 10000, isCancelled: () => cancelled },
      );

      if (cancelled) {
        return;
      }

      if (!freshUrl) {
        console.warn('[Preview] Gave up waiting for the dev server — preview-url kept failing');

        // The blank preview is what every backend failure looks like from the
        // user's side — a dead sandbox, a dev server that never booted, a
        // build that stopped early. Server-side events say a component failed;
        // only this one says a person was left staring at nothing.
        capture(ANALYTICS_EVENTS.PREVIEW_UNAVAILABLE, {
          chatId: currentChatId,
          previousStatus: runtime.status.get(),
        });

        // A preview was expected (activePreview exists) but its dev server never
        // served after the bounded backoff — surface the recoverable error state
        // (→ Retry banner) instead of a silent dead preview + endless re-polling.
        // Post-DEV-155 this is also the readiness *timeout*: a pod stuck in
        // 'starting' whose server never comes up lands in terminal 'error' here.
        // Only downgrade a 'starting'/'running' pod so a legitimate
        // 'sleeping'/'booting' transition isn't stomped.
        const prevStatus = runtime.status.get();

        if (
          !cancelled &&
          (prevStatus === 'running' || prevStatus === 'starting') &&
          runtime.getChatId() === currentChatId
        ) {
          runtime.status.set('error');
        }

        return;
      }

      // Cache-bust with the reload nonce (bumped when a generation ends) so
      // a Preview that mounts after a build loads the fresh site instead of
      // a cached pre-build page. Read fresh here so no effect dep is needed.
      const nonce = workbenchStore.previewReloadNonce.get();
      setIframeUrl(nonce > 0 ? appendReloadParam(freshUrl, nonce) : freshUrl);
      setIframeLoaded(true);
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [activePreview, isStaticPreview]);

  // Reload preview when AI generation finishes
  const isStreaming = useStore(streamingState);
  const wasStreamingRef = useRef(false);

  // A build is in flight for THIS chat. `isStreaming` alone misses the gaps a
  // refresh creates (the moment between page load and stream reconnect, and
  // between prompt-send and first token) — `$activeGeneration` is server-backed
  // and survives a refresh, so the carousel covers those gaps instead of
  // flashing the "Start prompting." stub.
  const activeGeneration = useStore($activeGeneration);
  const isBuildingThisChat =
    isStreaming ||
    (!!activeGeneration && (activeGeneration.id === currentChatId || activeGeneration.urlId === currentChatId));

  // Reset the content flag when switching chats so a fresh/new chat covers its
  // own first build.
  useEffect(() => {
    setPreviewHasContent(false);
  }, [currentChatId]);

  // Primary signal (new apps): the router posts `dunea:preview-state`.
  // `ready` = real routes render fine — reveal immediately, even mid-build.
  // `error` / `empty` = broken or still-empty — (re-)cover while building, so
  // import errors, error-boundary stubs, and the Vite overlay never show
  // mid-build. The router re-posts `ready` once an HMR update fixes the app.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; state?: string } | null;

      if (data?.type !== 'dunea:preview-state') {
        return;
      }

      if (data.state === 'ready') {
        setPreviewHasContent(true);
      } else if (data.state === 'error' || data.state === 'empty') {
        setPreviewHasContent(false);
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Fallback (apps generated before the postMessage shipped, and any reopened
  // chat): a completed build is on screen when the iframe has loaded while no
  // build is in flight — treat that as content too, so the carousel doesn't
  // hang. A first build that produces nothing still flips this true once the
  // build ends, so it reveals the honest empty state instead of an infinite
  // spinner.
  useEffect(() => {
    if (iframeLoaded && !isBuildingThisChat) {
      setPreviewHasContent(true);
    }
  }, [iframeLoaded, isBuildingThisChat]);

  // The sandbox dev server runs with HMR OFF (template vite.config,
  // server.hmr=false), so the app in the iframe is a snapshot from load time —
  // mid-build it can never announce newly-built pages on its own. While the
  // carousel covers the build, quietly reload the iframe (invisible churn): the
  // first reload whose app renders a real page posts `ready` and reveals it
  // mid-build. Stops the moment content shows; the end-of-stream reload below
  // takes over when the build finishes.
  //
  // CRITICAL: a reload aborts the load already in flight. On a cold, busy pod a
  // load takes longer than the poll tick, so blind interval reloads would
  // interrupt forever and the app would never live long enough to post `ready`
  // (observed: preview only appeared after the build ended). Reload only after
  // the previous load FINISHED (the iframe onLoad zeroes the stamp), with a 30s
  // ceiling so a genuinely wedged load still gets kicked.
  const pendingLoadAtRef = useRef(0);

  useEffect(() => {
    if (!isBuildingThisChat || previewHasContent) {
      return undefined;
    }

    // The initial natural load may itself still be in flight — give it the
    // same grace instead of yanking it after the first tick.
    if (!iframeLoaded && !pendingLoadAtRef.current) {
      pendingLoadAtRef.current = Date.now();
    }

    const id = setInterval(() => {
      const el = iframeRef.current;

      // Never clobber a version-peek URL (same rule as the reload below).
      if (!el || !el.src || $peekVersion.get()) {
        return;
      }

      const pendingAt = pendingLoadAtRef.current;

      if (pendingAt && Date.now() - pendingAt < 30_000) {
        return; // previous load still in flight — let it finish
      }

      pendingLoadAtRef.current = Date.now();

      try {
        const url = new URL(el.src, window.location.origin);
        url.searchParams.set('_r', Date.now().toString());
        el.src = url.toString();
      } catch {
        /* unparsable src — skip this tick */
      }
    }, 5000);

    return () => clearInterval(id);
  }, [isBuildingThisChat, previewHasContent, iframeLoaded]);

  useEffect(() => {
    if (!wasStreamingRef.current || isStreaming || !iframeRef.current) {
      wasStreamingRef.current = isStreaming;
      return undefined;
    }

    wasStreamingRef.current = false;

    // Streaming just ended — wait for the dev server to finish rebuilding
    // before reloading, otherwise the iframe loads a broken mid-rebuild page.
    let cancelled = false;

    const reloadAfterReady = async () => {
      // Give Vite HMR a moment to pick up file changes
      await new Promise((r) => setTimeout(r, 1000));

      if (cancelled || !iframeRef.current) {
        return;
      }

      // Poll the health endpoint to confirm the dev server is responsive
      let serverReady = false;
      let freshUrl: string | null = null;

      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const res = await httpClient.get(`/api/sandbox/${currentChatId}/preview-url`);
          const candidate: string | null = res.data?.url ?? null;

          // The dev server can be mid-restart right as a turn ends — reloading
          // then lands the iframe on the browser's error page.
          if (
            !cancelled &&
            res.status === 200 &&
            (await isPreviewReachable(candidate ?? iframeRef.current?.src ?? ''))
          ) {
            serverReady = true;
            freshUrl = candidate;
            break;
          }
        } catch {
          // Not ready yet
        }

        if (cancelled) {
          return;
        }

        await new Promise((r) => setTimeout(r, 800));
      }

      if (cancelled) {
        return;
      }

      // If the server never responded, the sandbox pod may have gone down.
      // Try to bring it back up so the preview doesn't stay stuck loading.
      if (!serverReady && currentChatId) {
        try {
          const status = await runtime.checkStatus(currentChatId);

          if (cancelled) {
            return;
          }

          if (status === 'sleeping') {
            console.log('[Preview] Sandbox down after generation — rebooting');
            setIframeLoaded(false);
            await runtime.boot(currentChatId);

            // After boot, port events will trigger the normal preview
            // initialization flow (PreviewsStore → activePreview → polling).
            // No manual reload needed — just return and let it happen.
            return;
          }
        } catch (err) {
          console.error('[Preview] Failed to reboot sandbox:', err);
        }
      }

      // Server is responsive — reload iframe with cache-bust. If the pod was
      // recreated (snapshot restore), the port-forward URL changed: freshUrl
      // targets the new pod while the iframe's src may point at a dead port.
      // Skip while peeking an older version — this writes iframeRef.current.src
      // directly, which would clobber the peek URL underneath the banner.
      if (!cancelled && iframeRef.current && !$peekVersion.get()) {
        if (serverReady) {
          const url = pickReloadUrl(iframeRef.current.src, freshUrl, window.location.origin);

          url.searchParams.set('_r', Date.now().toString());
          iframeRef.current.src = url.toString();
        } else {
          // Pod is running but dev server unresponsive — try a plain reload
          iframeRef.current.src = iframeRef.current.src;
        }
      }
    };

    reloadAfterReady();

    return () => {
      cancelled = true;
    };
  }, [isStreaming, currentChatId]);

  // Load watchdog for the live preview. onLoad fires for the browser's own
  // error page too, and the "completed build = content" fallback above then
  // lifts the carousel over it — the user saw Chrome's grey sad-page icon with
  // no Retry, while the same URL opened fine in a new tab moments later. After
  // each load, confirm the host actually answered; if not, keep the carousel
  // up and reload with backoff, then surface the Retry banner.
  const loadCheckRef = useRef(0);
  const unreachableRetriesRef = useRef(0);
  const isBuildingRef = useRef(isBuildingThisChat);
  isBuildingRef.current = isBuildingThisChat;

  useEffect(() => {
    // New preview target — start the retry budget over.
    loadCheckRef.current++;
    unreachableRetriesRef.current = 0;
    setPreviewUnreachable(false);
  }, [iframeUrl]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    pendingLoadAtRef.current = 0;

    const src = iframeRef.current?.src;

    // Static captures are same-origin files, peeks are read-only, and mid-build
    // the carousel's own reload loop owns the iframe.
    if (!src || isStaticPreview || $peekVersion.get() || isBuildingRef.current) {
      return;
    }

    const token = ++loadCheckRef.current;

    void isPreviewReachable(src).then((reachable) => {
      if (token !== loadCheckRef.current) {
        return;
      }

      if (reachable) {
        unreachableRetriesRef.current = 0;
        setPreviewUnreachable(false);

        return;
      }

      setPreviewUnreachable(true);

      const attempt = unreachableRetriesRef.current++;

      if (attempt >= 5) {
        console.warn('[Preview] Preview host kept refusing connections — giving up');
        capture(ANALYTICS_EVENTS.PREVIEW_UNAVAILABLE, {
          chatId: currentChatId,
          previousStatus: runtime.status.get(),
        });

        const prevStatus = runtime.status.get();

        if ((prevStatus === 'running' || prevStatus === 'starting') && runtime.getChatId() === currentChatId) {
          runtime.status.set('error');
        }

        return;
      }

      setTimeout(
        () => {
          const el = iframeRef.current;

          if (token !== loadCheckRef.current || !el || $peekVersion.get()) {
            return;
          }

          try {
            const url = new URL(el.src, window.location.origin);
            url.searchParams.set('_r', Date.now().toString());
            el.src = url.toString();
          } catch {
            /* unparsable src — leave it */
          }
        },
        Math.min(1500 * 2 ** attempt, 10_000),
      );
    });
  }, [isStaticPreview, currentChatId]);

  // --- element selector bridge ---
  const selectMode = useStore($selectMode);
  const selectorAvailable = useStore($selectorAvailable);

  // Plugin panels / admin extensions asked to open an app path (panel.navigate('/…')).
  // The request is usually made from a tool tab, so this Preview mounts AFTER
  // it: subscribe (fires with the pending value) and consume it once the
  // iframe has a src.
  useEffect(
    () =>
      $previewNavigation.subscribe((nav) => {
        const el = iframeRef.current;

        if (!nav || !iframeUrl || !el?.src || $peekVersion.get()) {
          return;
        }

        const next = previewNavigationUrl(el.src, nav.path, window.location.origin);

        $previewNavigation.set(null);

        if (next) {
          el.src = next;
        }
      }),
    [iframeUrl],
  );

  // --- direct edits: apply deterministically, fall back to an agent turn ---
  // One at a time — a second save racing the first would patch a stale read.
  const directEditBusy = useRef(false);

  const reloadAfterDirectEdit = useCallback(() => {
    // hmr is off in the template, so the iframe never picks up the write on
    // its own — give Vite a beat to invalidate, then reload the live preview.
    // Use a fresh cache-buster param: re-assigning the same src can be a no-op
    // (leaving the stale DOM, so a re-selected element still reports its old
    // classes and the next direct edit can't find them in the rewritten source).
    setTimeout(() => {
      if (iframeRef.current && !$peekVersion.get()) {
        const url = new URL(iframeRef.current.src, window.location.origin);
        url.searchParams.set('_r', Date.now().toString());
        iframeRef.current.src = url.toString();
      }
    }, 800);
  }, []);

  const handleTextEdited = useCallback(
    async (oldText: string, newText: string) => {
      const sel = $selectedElement.get();
      const chat = chatId.get();

      if (!sel || !chat || directEditBusy.current) {
        return;
      }

      directEditBusy.current = true;

      try {
        const result = await applyTextEditDirect(chat, sel, oldText, newText);
        trackVisualEdit('preview', 'text', result);

        // The iframe already shows the new text (contentEditable committed it),
        // so on success we do nothing loud — no reload flash, no toast, and
        // never a chat/agent turn. The edit is silently persisted to the file.
        if (!result.ok) {
          // Couldn't patch the source safely — reload to drop the un-persisted
          // visual change so the preview stays honest. Still no agent.
          reloadAfterDirectEdit();
          toast.error("Couldn't save that text change");
        }
      } finally {
        directEditBusy.current = false;
      }
    },
    [reloadAfterDirectEdit],
  );

  const handleStyleSave = useCallback(
    async (selection: SelectedElementPayload, deltas: StyleDeltas) => {
      const chat = chatId.get();

      if (!chat || Object.keys(deltas).length === 0) {
        return;
      }

      // Background auto-save can fire while a previous save is still in flight
      // (rapid swatch clicks). Never drop the edit and never hand it to the
      // agent — just retry once the lane frees up.
      if (directEditBusy.current) {
        setTimeout(() => handleStyleSave(selection, deltas), 200);

        return;
      }

      directEditBusy.current = true;

      try {
        const result = await applyStyleEditDirect(chat, selection, deltas);
        trackVisualEdit('preview', 'style', result, 'sent_to_ai');

        if (result.ok) {
          // Re-anchor the selection to the className we just wrote so the NEXT
          // edit on this same element searches for the current literal, not the
          // stale pre-edit one (which the write already replaced). Mutate in
          // place — replacing the store object would reset the open style panel.
          if (result.classes) {
            selection.classes = result.classes;

            const current = $selectedElement.get();

            if (current && current !== selection) {
              current.classes = result.classes;
            }
          }

          $styleDeltas.set({});

          // Reload the preview so the saved Tailwind class is actually applied in
          // the iframe. The inline live-preview gets wiped when the element is
          // deselected, and the class isn't in the running DOM until a reload —
          // so without this the change only appears after a manual refresh.
          reloadAfterDirectEdit();
        } else {
          // The deterministic edit couldn't anchor — the element has no static
          // className (nothing to rewrite), its classes/props are dynamic, or the
          // change has no Tailwind mapping. Hand it to the agent instead of
          // erroring out — the same graceful fallback the image swap uses — so
          // the change still lands. Chat.client picks up $elementEditFallback.
          $elementEditFallback.set(buildStyleEditPrompt(selection, deltas));
          $styleDeltas.set({});
          toast('Applying this style change with the assistant…');
        }
      } finally {
        directEditBusy.current = false;
      }
    },
    [reloadAfterDirectEdit],
  );

  const handleReplaceImage = useCallback(
    async (file: File) => {
      const sel = $selectedElement.get();
      const chat = chatId.get();

      if (!sel || !chat || directEditBusy.current) {
        return;
      }

      directEditBusy.current = true;

      try {
        const upload = await apiUploadStorageFile(chat, file);
        const result = await applyImageSwapDirect(chat, sel, upload.url);
        trackVisualEdit('preview', 'image', result, 'sent_to_ai');

        if (result.ok) {
          toast.success('Image replaced');
          reloadAfterDirectEdit();
        } else {
          $elementEditFallback.set(buildImageSwapFallbackPrompt(sel, upload.url));
          toast.info('That edit needs the AI — sent to chat');
        }
      } catch {
        toast.error('Image upload failed');
      } finally {
        directEditBusy.current = false;
      }
    },
    [reloadAfterDirectEdit],
  );

  useEffect(() => {
    // Static prod builds ship without fiber debug info, so the selector must
    // stay dormant — never reply INIT and never spin up the bridge listener.
    if (!iframeUrl || isStaticPreview) {
      return undefined;
    }

    const origin = previewOrigin(iframeUrl);

    // A relative/unparseable iframeUrl (transient during Resume) has no real
    // preview origin to bridge to — bail rather than let new URL() throw.
    if (!origin) {
      return undefined;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data as { type?: string; payload?: SelectedElementPayload };

      if (data?.type === 'DUNEA_SELECTOR_READY') {
        iframeRef.current?.contentWindow?.postMessage({ type: 'DUNEA_SELECTOR_INIT' }, origin);

        // Resync client state with the freshly booted iframe — a stale
        // selection can't survive an iframe reboot.
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'DUNEA_SELECT_MODE', enabled: $selectMode.get() },
          origin,
        );

        if ($selectedElement.get()) {
          clearSelection();
        }

        $selectorAvailable.set(true);
      } else if (data?.type === 'DUNEA_ELEMENT_SELECTED' && isValidSelectionPayload(data.payload)) {
        setSelectedElement(data.payload);
        trackElementSelected('preview', data.payload.tag);
      } else if (data?.type === 'DUNEA_TEXT_EDITED') {
        const edit = (event.data as { payload?: { oldText?: unknown; newText?: unknown } }).payload;

        if (
          edit &&
          typeof edit.oldText === 'string' &&
          edit.oldText.length <= 2048 &&
          typeof edit.newText === 'string' &&
          edit.newText.length <= 2048
        ) {
          handleTextEdited(edit.oldText, edit.newText);
        }
      } else if (data?.type === 'DUNEA_SELECT_MODE_EXITED') {
        $selectMode.set(false);
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      $selectorAvailable.set(false);
      clearSelection();
    };
  }, [iframeUrl, isStaticPreview, handleTextEdited]);

  // push mode changes down to the iframe
  useEffect(() => {
    const origin = iframeUrl ? previewOrigin(iframeUrl) : null;

    if (!origin || !selectorAvailable) {
      return;
    }

    iframeRef.current?.contentWindow?.postMessage({ type: 'DUNEA_SELECT_MODE', enabled: selectMode }, origin);
  }, [selectMode, selectorAvailable, iframeUrl]);

  const postToPreview = useCallback(
    (msg: unknown) => {
      const origin = iframeUrl ? previewOrigin(iframeUrl) : null;

      if (!origin) {
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(msg, origin);
    },
    [iframeUrl],
  );

  const postStylePreview = useCallback(
    (styles: Record<string, string>) => postToPreview({ type: 'DUNEA_STYLE_PREVIEW', styles }),
    [postToPreview],
  );
  const postStyleReset = useCallback(() => postToPreview({ type: 'DUNEA_STYLE_RESET' }), [postToPreview]);

  const findMinPortIndex = useCallback(
    (minIndex: number, preview: { port: number }, index: number, array: { port: number }[]) => {
      return preview.port < array[minIndex].port ? index : minIndex;
    },
    [],
  );

  useEffect(() => {
    if (previews.length > 1 && !hasSelectedPreview.current) {
      const minPortIndex = previews.reduce(findMinPortIndex, 0);
      setActivePreviewIndex(minPortIndex);
    }
  }, [previews, findMinPortIndex]);

  const reloadPreview = () => {
    // While peeking, the visible iframe is showing peekUrl, not the live src
    // this would reload — reloading here would just clobber it with itself.
    if (iframeRef.current && !peekVersion) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const toggleFullscreen = () => {
    chatStore.setKey('showChat', !chatStore.get().showChat);
  };

  const toggleDeviceMode = () => {
    setIsDeviceModeOn((prev) => !prev);
  };

  useEffect(() => {
    if (isNarrow) {
      setIsDeviceModeOn(false);
    }
  }, [isNarrow]);

  const startResizing = (e: React.PointerEvent, side: ResizeSide) => {
    if (!isDeviceModeOn) {
      return;
    }

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    resizingState.current = {
      isResizing: true,
      side,
      startX: e.clientX,
      startWidthPercent: widthPercent,
      windowWidth: window.innerWidth,
      pointerId: e.pointerId,
    };
  };

  const ResizeHandle = ({ side }: { side: ResizeSide }) => {
    if (!side) {
      return null;
    }

    return (
      <div
        className={`resize-handle-${side}`}
        onPointerDown={(e) => startResizing(e, side)}
        style={{
          position: 'absolute',
          top: 0,
          ...(side === 'left' ? { left: 0, marginLeft: '-7px' } : { right: 0, marginRight: '-7px' }),
          width: '15px',
          height: '100%',
          cursor: 'ew-resize',
          background: 'var(--dunea-elements-background-depth-4, rgba(0,0,0,.3))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s',
          userSelect: 'none',
          touchAction: 'none',
          zIndex: 10,
        }}
        onMouseOver={(e) =>
          (e.currentTarget.style.background = 'var(--dunea-elements-background-depth-4, rgba(0,0,0,.3))')
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.background = 'var(--dunea-elements-background-depth-3, rgba(0,0,0,.15))')
        }
        title="Drag to resize width"
      >
        <GripIcon />
      </div>
    );
  };

  useEffect(() => {
    // Skip if not in device mode
    if (!isDeviceModeOn) {
      return;
    }

    const handlePointerMove = (e: PointerEvent) => {
      const state = resizingState.current;

      if (!state.isResizing || e.pointerId !== state.pointerId) {
        return;
      }

      const dx = e.clientX - state.startX;
      const dxPercent = (dx / state.windowWidth) * 100 * SCALING_FACTOR;

      let newWidthPercent = state.startWidthPercent;

      if (state.side === 'right') {
        newWidthPercent = state.startWidthPercent + dxPercent;
      } else if (state.side === 'left') {
        newWidthPercent = state.startWidthPercent - dxPercent;
      }

      // Limit width percentage between 10% and 90%
      newWidthPercent = Math.max(10, Math.min(newWidthPercent, 90));

      // Force a synchronous update to ensure the UI reflects the change immediately
      setWidthPercent(newWidthPercent);

      // Calculate and update the actual pixel width
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const newWidth = Math.round((containerWidth * newWidthPercent) / 100);
        setCurrentWidth(newWidth);

        // Apply the width directly to the container for immediate feedback
        const previewContainer = containerRef.current.querySelector('div[style*="width"]');

        if (previewContainer) {
          (previewContainer as HTMLElement).style.width = `${newWidthPercent}%`;
        }
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      const state = resizingState.current;

      if (!state.isResizing || e.pointerId !== state.pointerId) {
        return;
      }

      // Find all resize handles
      const handles = document.querySelectorAll('.resize-handle-left, .resize-handle-right');

      // Release pointer capture from any handle that has it
      handles.forEach((handle) => {
        if ((handle as HTMLElement).hasPointerCapture?.(e.pointerId)) {
          (handle as HTMLElement).releasePointerCapture(e.pointerId);
        }
      });

      // Reset state
      resizingState.current = {
        ...resizingState.current,
        isResizing: false,
        side: null,
        pointerId: null,
      };

      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    // Add event listeners
    document.addEventListener('pointermove', handlePointerMove, { passive: false });
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);

    // Define cleanup function
    function cleanupResizeListeners() {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);

      // Release any lingering pointer captures
      if (resizingState.current.pointerId !== null) {
        const handles = document.querySelectorAll('.resize-handle-left, .resize-handle-right');
        handles.forEach((handle) => {
          if ((handle as HTMLElement).hasPointerCapture?.(resizingState.current.pointerId!)) {
            (handle as HTMLElement).releasePointerCapture(resizingState.current.pointerId!);
          }
        });

        // Reset state
        resizingState.current = {
          ...resizingState.current,
          isResizing: false,
          side: null,
          pointerId: null,
        };

        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    }

    // Return the cleanup function
    // eslint-disable-next-line consistent-return
    return cleanupResizeListeners;
  }, [isDeviceModeOn, SCALING_FACTOR]);

  useEffect(() => {
    const handleWindowResize = () => {
      // Update the window width in the resizing state
      resizingState.current.windowWidth = window.innerWidth;

      // Update the current width in pixels
      if (containerRef.current && isDeviceModeOn) {
        const containerWidth = containerRef.current.clientWidth;
        setCurrentWidth(Math.round((containerWidth * widthPercent) / 100));
      }
    };

    window.addEventListener('resize', handleWindowResize);

    // Initial calculation of current width
    if (containerRef.current && isDeviceModeOn) {
      const containerWidth = containerRef.current.clientWidth;
      setCurrentWidth(Math.round((containerWidth * widthPercent) / 100));
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isDeviceModeOn, widthPercent]);

  // Update current width when device mode is toggled
  useEffect(() => {
    if (containerRef.current && isDeviceModeOn) {
      const containerWidth = containerRef.current.clientWidth;
      setCurrentWidth(Math.round((containerWidth * widthPercent) / 100));
    }
  }, [isDeviceModeOn]);

  const GripIcon = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          color: 'var(--dunea-elements-textSecondary, rgba(0,0,0,0.5))',
          fontSize: '10px',
          lineHeight: '5px',
          userSelect: 'none',
          marginLeft: '1px',
        }}
      >
        ••• •••
      </div>
    </div>
  );

  const openInNewWindow = (size: WindowSize) => {
    if (activePreview?.baseUrl) {
      const previewUrl = activePreview.baseUrl;

      if (previewUrl) {
        // Adjust dimensions for landscape mode if applicable
        let width = size.width;
        let height = size.height;

        if (isLandscape && (size.frameType === 'mobile' || size.frameType === 'tablet')) {
          // Swap width and height for landscape mode
          width = size.height;
          height = size.width;
        }

        // Create a window with device frame if enabled
        if (showDeviceFrame && size.hasFrame) {
          // Calculate frame dimensions
          const frameWidth = size.frameType === 'mobile' ? (isLandscape ? 120 : 40) : 60; // Width padding on each side
          const frameHeight = size.frameType === 'mobile' ? (isLandscape ? 80 : 80) : isLandscape ? 60 : 100; // Height padding on top and bottom

          // Create a window with the correct dimensions first
          const newWindow = window.open(
            '',
            '_blank',
            `width=${width + frameWidth},height=${height + frameHeight + 40},menubar=no,toolbar=no,location=no,status=no`,
          );

          if (!newWindow) {
            console.error('Failed to open new window');
            return;
          }

          // Create the HTML content for the frame
          const frameColor = getFrameColor();
          const frameRadius = size.frameType === 'mobile' ? '36px' : '20px';
          const framePadding =
            size.frameType === 'mobile'
              ? isLandscape
                ? '40px 60px'
                : '40px 20px'
              : isLandscape
                ? '30px 50px'
                : '50px 30px';

          // Position notch and home button based on orientation
          const notchTop = isLandscape ? '50%' : '20px';
          const notchLeft = isLandscape ? '30px' : '50%';
          const notchTransform = isLandscape ? 'translateY(-50%)' : 'translateX(-50%)';
          const notchWidth = isLandscape ? '8px' : size.frameType === 'mobile' ? '60px' : '80px';
          const notchHeight = isLandscape ? (size.frameType === 'mobile' ? '60px' : '80px') : '8px';

          const homeBottom = isLandscape ? '50%' : '15px';
          const homeRight = isLandscape ? '30px' : '50%';
          const homeTransform = isLandscape ? 'translateY(50%)' : 'translateX(50%)';
          const homeWidth = isLandscape ? '4px' : '40px';
          const homeHeight = isLandscape ? '40px' : '4px';

          // Create HTML content for the wrapper page
          const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="utf-8">
              <title>${size.name} Preview</title>
              <style>
                body {
                  margin: 0;
                  padding: 0;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 100vh;
                  background: #f0f0f0;
                  overflow: hidden;
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }
                
                .device-container {
                  position: relative;
                }
                
                .device-name {
                  position: absolute;
                  top: -30px;
                  left: 0;
                  right: 0;
                  text-align: center;
                  font-size: 14px;
                  color: #333;
                }
                
                .device-frame {
                  position: relative;
                  border-radius: ${frameRadius};
                  background: ${frameColor};
                  padding: ${framePadding};
                  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                  overflow: hidden;
                }
                
                /* Notch */
                .device-frame:before {
                  content: '';
                  position: absolute;
                  top: ${notchTop};
                  left: ${notchLeft};
                  transform: ${notchTransform};
                  width: ${notchWidth};
                  height: ${notchHeight};
                  background: #333;
                  border-radius: 4px;
                  z-index: 2;
                }
                
                /* Home button */
                .device-frame:after {
                  content: '';
                  position: absolute;
                  bottom: ${homeBottom};
                  right: ${homeRight};
                  transform: ${homeTransform};
                  width: ${homeWidth};
                  height: ${homeHeight};
                  background: #333;
                  border-radius: 50%;
                  z-index: 2;
                }
                
                iframe {
                  border: none;
                  width: ${width}px;
                  height: ${height}px;
                  background: white;
                  display: block;
                }
              </style>
            </head>
            <body>
              <div class="device-container">
                <div class="device-name">${size.name} ${isLandscape ? '(Landscape)' : '(Portrait)'}</div>
                <div class="device-frame">
                  <iframe src="${sanitizeUrlForHtml(previewUrl)}"></iframe>
                </div>
              </div>
            </body>
            </html>
          `;

          // Write the HTML content to the new window
          newWindow.document.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          // Standard window without frame
          const newWindow = window.open(
            previewUrl,
            '_blank',
            `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`,
          );

          if (newWindow) {
            newWindow.focus();
          }
        }
      } else {
        console.warn('[Preview] Invalid sandbox URL:', activePreview.baseUrl);
      }
    }
  };

  const openInNewTab = async () => {
    // The stored baseUrl is a local port-forward address that can go stale (the
    // port changes when the sandbox proxy is recreated), so re-fetch a live URL
    // at click time — same self-heal the iframe already does — before opening.
    const fallback = activePreview?.baseUrl;

    if (currentChatId) {
      try {
        const res = await httpClient.get(`/api/sandbox/${currentChatId}/preview-url`);
        const fresh = res.data?.url || fallback;

        if (fresh) {
          window.open(fresh, '_blank');
          return;
        }
      } catch {
        // Fall through to the cached URL below.
      }
    }

    if (fallback) {
      window.open(fallback, '_blank');
    }
  };

  // Function to get the correct frame padding based on orientation
  const getFramePadding = useCallback(() => {
    if (!selectedWindowSize) {
      return '40px 20px';
    }

    const isMobile = selectedWindowSize.frameType === 'mobile';

    if (isLandscape) {
      // Increase horizontal padding in landscape mode to ensure full device frame is visible
      return isMobile ? '40px 60px' : '30px 50px';
    }

    return isMobile ? '40px 20px' : '50px 30px';
  }, [isLandscape, selectedWindowSize]);

  // Outer size of the in-panel device frame (border-box, so the bezel padding is included).
  const deviceFrameWidth = isLandscape
    ? selectedWindowSize.height + (selectedWindowSize.frameType === 'mobile' ? 120 : 60)
    : selectedWindowSize.width + (selectedWindowSize.frameType === 'mobile' ? 40 : 60);
  const deviceFrameHeight = isLandscape
    ? selectedWindowSize.width + (selectedWindowSize.frameType === 'mobile' ? 80 : 60)
    : selectedWindowSize.height + (selectedWindowSize.frameType === 'mobile' ? 80 : 100);

  // The wrapper only exists while there's something to preview — re-measure when it (re)mounts,
  // e.g. after a sandbox sleep/wake, or device mode switched on before the preview was ready.
  const hasPreviewSurface = !!((activePreview && iframeUrl) || peekUrl);

  // Scale the frame down to fit the panel. At a fixed scale of 1 a tablet frame
  // overflowed its centered flex box, whose top/left overflow can't be scrolled to.
  useEffect(() => {
    const wrapper = deviceWrapperRef.current;

    if (!isDeviceModeOn || !showDeviceFrameInPreview || !wrapper) {
      return undefined;
    }

    const FRAME_MARGIN = 40;
    const fit = () => {
      const availableWidth = wrapper.clientWidth - FRAME_MARGIN * 2;
      const availableHeight = wrapper.clientHeight - FRAME_MARGIN * 2;
      setDeviceScale(
        Math.max(0.2, Math.min(1, availableWidth / deviceFrameWidth, availableHeight / deviceFrameHeight)),
      );
    };

    fit();

    const observer = new ResizeObserver(fit);
    observer.observe(wrapper);

    return () => observer.disconnect();
  }, [isDeviceModeOn, showDeviceFrameInPreview, deviceFrameWidth, deviceFrameHeight, hasPreviewSurface]);

  // Function to get the frame color based on dark mode
  const getFrameColor = useCallback(() => {
    // Check if the document has a dark class or data-theme="dark"
    const isDarkMode =
      document.documentElement.classList.contains('dark') ||
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Return a darker color for light mode, lighter color for dark mode
    return isDarkMode ? '#555' : '#111';
  }, []);

  // Effect to handle color scheme changes
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleColorSchemeChange = () => {
      // Force a re-render when color scheme changes
      if (showDeviceFrameInPreview) {
        setShowDeviceFrameInPreview(true);
      }
    };

    darkModeMediaQuery.addEventListener('change', handleColorSchemeChange);

    return () => {
      darkModeMediaQuery.removeEventListener('change', handleColorSchemeChange);
    };
  }, [showDeviceFrameInPreview]);

  const canvasMode = useStore($canvasMode);

  return (
    <div ref={containerRef} className={`w-full h-full flex flex-col relative`}>
      {/* Canvas Mode - fullscreen overlay when active */}
      {canvasMode && iframeUrl && currentChatId && (
        <CanvasMode previewUrl={iframeUrl} chatId={currentChatId} onReloadPreview={reloadPreview} />
      )}

      {!minimal && (
        <div className="bg-surface border-b border-hairline px-2 sm:px-3 py-2 flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={reloadPreview}
            className="ui-btn-icon"
            title="Reload preview"
            aria-label="Reload preview"
          >
            <div className="i-ph:arrow-clockwise text-base" />
          </button>

          {/* Read-only URL — grows to fill the row like an address bar, click to copy.
              Hidden on phones, where it would shrink to just the lock icon. */}
          <div className="hidden sm:flex flex-1 items-center min-w-0 px-3">
            {(() => {
              if (!iframeUrl) {
                return null;
              }

              let host: string;
              let path: string;

              try {
                const u = new URL(iframeUrl);

                if (u.protocol !== 'http:' && u.protocol !== 'https:') {
                  return null;
                }

                host = u.host;
                path = u.pathname === '/' ? '' : u.pathname;
              } catch {
                return null;
              }

              return (
                <button
                  type="button"
                  title="Copy preview URL"
                  onClick={() => {
                    navigator.clipboard?.writeText(iframeUrl).then(
                      () => {
                        setUrlCopied(true);
                        window.setTimeout(() => setUrlCopied(false), 1200);
                      },
                      () => undefined,
                    );
                  }}
                  className="group flex items-center gap-2 w-full h-8 min-w-0 bg-sunken border border-hairline hover:border-prominent rounded-md px-3 text-[12px] font-geist-mono transition-colors"
                >
                  <div
                    className={classNames(
                      'flex-shrink-0 text-[12px]',
                      urlCopied ? 'i-ph:check-bold text-success' : 'i-ph:lock-simple-fill text-success/70',
                    )}
                  />
                  <span className="truncate text-secondary">
                    <span className="text-muted">https://</span>
                    {host}
                    {path}
                  </span>
                  <div className="i-ph:copy flex-shrink-0 ml-auto text-[12px] text-muted opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity" />
                </button>
              );
            })()}
          </div>
          <div className="flex-1 sm:hidden" />

          {/* Named toolbar — every tool labelled, grouped edit / view */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <VersionSelector chatId={chatId.get() ?? ''} />
            {/* Touch opens "Open" straight into a new tab, so the device picker inside that menu is unreachable — hide Device too. */}
            {!isNarrow && !isTouch && (
              <Tooltip
                content={
                  isDeviceModeOn
                    ? 'Device Mode - Click to switch to responsive view'
                    : 'Device Mode - Preview as iPhone, iPad, or other devices'
                }
                side="bottom"
                delayDuration={100}
              >
                <button
                  type="button"
                  onClick={toggleDeviceMode}
                  aria-label="Device mode"
                  className={classNames(
                    'inline-flex items-center gap-1.5 h-8 touch:h-10 px-2.5 rounded-md text-[12.5px] font-medium transition-colors',
                    isDeviceModeOn
                      ? 'text-accent bg-accent/[0.08]'
                      : 'text-secondary hover:bg-white/[0.04] hover:text-primary',
                  )}
                >
                  <div className="i-ph:devices text-[15px]" />
                  <span className={TOOLBAR_LABEL}>Device</span>
                </button>
              </Tooltip>
            )}

            {/* Scan-to-preview is pointless when the user is already on a phone or tablet. */}
            {expoUrl && !isTouch && (
              <Tooltip content="QR Code - Scan to preview on your mobile device" side="bottom" delayDuration={100}>
                <button
                  type="button"
                  onClick={() => setIsExpoQrModalOpen(true)}
                  className="ui-btn-icon"
                  aria-label="Show QR code"
                >
                  <div className="i-ph:qr-code text-base" />
                </button>
              </Tooltip>
            )}

            <ExpoQrModal open={isExpoQrModalOpen} onClose={() => setIsExpoQrModalOpen(false)} />

            {isDeviceModeOn && (
              <>
                <Tooltip
                  content={
                    isLandscape ? 'Rotate - Switch to portrait orientation' : 'Rotate - Switch to landscape orientation'
                  }
                  side="bottom"
                  delayDuration={100}
                >
                  <button
                    type="button"
                    onClick={() => setIsLandscape(!isLandscape)}
                    className="ui-btn-icon"
                    aria-label="Rotate device"
                  >
                    <div className="i-ph:device-rotate text-base" />
                  </button>
                </Tooltip>
                <Tooltip
                  content={
                    showDeviceFrameInPreview
                      ? 'Device Frame - Click to hide phone bezel'
                      : 'Device Frame - Click to show phone bezel'
                  }
                  side="bottom"
                  delayDuration={100}
                >
                  <button
                    type="button"
                    onClick={() => setShowDeviceFrameInPreview(!showDeviceFrameInPreview)}
                    className="ui-btn-icon"
                    aria-label={showDeviceFrameInPreview ? 'Hide device frame' : 'Show device frame'}
                  >
                    <div
                      className={classNames(
                        'text-base',
                        showDeviceFrameInPreview ? 'i-ph:device-mobile' : 'i-ph:device-mobile-slash',
                      )}
                    />
                  </button>
                </Tooltip>
              </>
            )}
            {selectorAvailable && !isNarrow && (
              <Tooltip
                content="Canvas Mode - Click to visually edit colors, fonts, spacing, and more"
                side="bottom"
                delayDuration={100}
              >
                <button
                  type="button"
                  onClick={enterCanvasMode}
                  className="inline-flex items-center gap-1.5 h-8 touch:h-10 px-2.5 rounded-md text-[12.5px] font-medium text-secondary hover:bg-white/[0.04] hover:text-primary transition-colors"
                >
                  <div className="i-ph:paint-brush-broad text-[15px]" />
                  <span className={TOOLBAR_LABEL}>Canvas</span>
                </button>
              </Tooltip>
            )}
            {!isNarrow && <div className="w-px h-5 bg-hairline mx-1" />}

            {/* Below lg the preview is already full-screen, and collapsing the chat
                there left a blank screen once the overlay was dismissed. */}
            {!isCompact && (
              <Tooltip
                content={isFullscreen ? 'Minimize — back to split view' : 'Full screen — expand the preview'}
                side="bottom"
                delayDuration={100}
              >
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? 'Minimize preview' : 'Full screen preview'}
                  className="inline-flex items-center gap-1.5 h-8 touch:h-10 px-2.5 rounded-md text-[12.5px] font-medium text-secondary hover:bg-white/[0.04] hover:text-primary transition-colors"
                >
                  <div className={classNames('text-[15px]', isFullscreen ? 'i-ph:arrows-in' : 'i-ph:arrows-out')} />
                  <span className={TOOLBAR_LABEL}>{isFullscreen ? 'Minimize' : 'Full screen'}</span>
                </button>
              </Tooltip>
            )}

            <div className="flex items-center relative">
              <Tooltip
                content="Open in New Window - Preview settings and window options"
                side="bottom"
                delayDuration={100}
              >
                <button
                  type="button"
                  // On phones/tablets the settings panel's device-sized windows can't
                  // work (mobile browsers ignore window.open sizes) — just open a tab.
                  onClick={() =>
                    isNarrow || isTouch ? void openInNewTab() : setIsWindowSizeDropdownOpen(!isWindowSizeDropdownOpen)
                  }
                  aria-label="Open preview in new tab"
                  className="inline-flex items-center gap-1.5 h-8 touch:h-10 px-2.5 rounded-md text-[12.5px] font-medium text-secondary hover:bg-white/[0.04] hover:text-primary transition-colors"
                >
                  <div className="i-ph:arrow-square-out text-[15px]" />
                  <span className={TOOLBAR_LABEL}>Open</span>
                </button>
              </Tooltip>

              {isWindowSizeDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-50" onClick={() => setIsWindowSizeDropdownOpen(false)} />
                  <div
                    className={classNames(
                      'absolute right-0 top-full mt-2 z-50 w-[300px] max-w-[calc(100vw-1.5rem)] max-h-[72dvh] overflow-y-auto',
                      'bg-surface/80 backdrop-blur-2xl border border-subtle rounded-[22px]',
                      'shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_8px_24px_-16px_rgba(0,0,0,0.6)]',
                      'origin-top-right animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-150 ease-out',
                    )}
                  >
                    <div className="flex flex-col gap-7 p-6">
                      <h3 className="text-[15px] font-semibold text-primary font-geist tracking-[-0.01em]">
                        Preview Settings
                      </h3>

                      {/* Open in new tab — calm full-width action */}
                      <button
                        type="button"
                        onClick={() => {
                          openInNewTab();
                          setIsWindowSizeDropdownOpen(false);
                        }}
                        className={classNames(
                          'group w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl',
                          'border border-subtle bg-white/[0.02] text-secondary',
                          'transition-colors duration-150 ease-out',
                          'hover:text-primary hover:bg-white/[0.04]',
                        )}
                      >
                        <span className="text-[13px] font-medium">Open preview in new tab</span>
                        <div className="i-ph:arrow-square-out text-base text-muted group-hover:text-secondary transition-colors duration-150" />
                      </button>

                      {/* Appearance */}
                      <div className="flex flex-col gap-3.5">
                        <span className="text-[10.5px] font-medium text-muted uppercase tracking-[0.08em]">
                          Appearance
                        </span>
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-secondary">Show device frame</span>
                          <button
                            className={classNames('ui-switch', showDeviceFrame && 'ui-switch-on')}
                            aria-pressed={showDeviceFrame}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeviceFrame(!showDeviceFrame);
                            }}
                          >
                            <span className={classNames('ui-switch-thumb', showDeviceFrame && 'ui-switch-thumb-on')} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-secondary">Landscape mode</span>
                          <button
                            className={classNames('ui-switch', isLandscape && 'ui-switch-on')}
                            aria-pressed={isLandscape}
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsLandscape(!isLandscape);
                            }}
                          >
                            <span className={classNames('ui-switch-thumb', isLandscape && 'ui-switch-thumb-on')} />
                          </button>
                        </div>
                      </div>

                      {/* Device — categorized single-open accordion */}
                      <div className="flex flex-col gap-2.5">
                        <span className="text-[10.5px] font-medium text-muted uppercase tracking-[0.08em]">Device</span>
                        {DEVICE_CATEGORIES.map((cat) => (
                          <DeviceCategorySection
                            key={cat.key}
                            meta={cat}
                            devices={WINDOW_SIZES.filter((s) => s.category === cat.key)}
                            expanded={expandedDeviceCategory === cat.key}
                            onToggle={() => setExpandedDeviceCategory((prev) => (prev === cat.key ? null : cat.key))}
                            selectedName={selectedWindowSize.name}
                            landscape={isLandscape}
                            framed={showDeviceFrame}
                            onSelect={(size) => {
                              setSelectedWindowSize(size);
                              setIsWindowSizeDropdownOpen(false);
                              openInNewWindow(size);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {currentChatId && (
              <>
                <div className="hidden sm:block w-px h-5 bg-hairline mx-1" />
                <SharePreviewButton chatId={currentChatId} buildDone={!isBuildingThisChat} />
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 flex justify-center items-center overflow-auto bg-page">
        <div
          style={{
            width: isDeviceModeOn ? (showDeviceFrameInPreview ? '100%' : `${widthPercent}%`) : '100%',
            height: '100%',
            overflow: 'auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {(activePreview && iframeUrl) || peekUrl ? (
            <>
              {isDeviceModeOn && showDeviceFrameInPreview ? (
                <div
                  ref={deviceWrapperRef}
                  className="device-wrapper"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    padding: '0',
                    overflow: 'auto',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                  }}
                >
                  {/* Sizer takes the frame's scaled footprint so the flex centering
                      and scroll extents match what's actually drawn. */}
                  <div
                    style={{
                      position: 'relative',
                      flexShrink: 0,
                      width: `${deviceFrameWidth * deviceScale}px`,
                      height: `${deviceFrameHeight * deviceScale}px`,
                    }}
                  >
                    <div
                      className="device-frame-container"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        borderRadius: selectedWindowSize.frameType === 'mobile' ? '36px' : '20px',
                        background: getFrameColor(),
                        padding: getFramePadding(),
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        overflow: 'hidden',
                        transform: `scale(${deviceScale})`,
                        transformOrigin: 'top left',
                        transition: 'transform 0.3s ease',
                        width: `${deviceFrameWidth}px`,
                        height: `${deviceFrameHeight}px`,
                      }}
                    >
                      {/* Notch - positioned based on orientation */}
                      <div
                        style={{
                          position: 'absolute',
                          top: isLandscape ? '50%' : '20px',
                          left: isLandscape ? '30px' : '50%',
                          transform: isLandscape ? 'translateY(-50%)' : 'translateX(-50%)',
                          width: isLandscape ? '8px' : selectedWindowSize.frameType === 'mobile' ? '60px' : '80px',
                          height: isLandscape ? (selectedWindowSize.frameType === 'mobile' ? '60px' : '80px') : '8px',
                          background: '#333',
                          borderRadius: '4px',
                          zIndex: 2,
                        }}
                      />

                      {/* Home button - positioned based on orientation */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: isLandscape ? '50%' : '15px',
                          right: isLandscape ? '30px' : '50%',
                          transform: isLandscape ? 'translateY(50%)' : 'translateX(50%)',
                          width: isLandscape ? '4px' : '40px',
                          height: isLandscape ? '40px' : '4px',
                          background: '#333',
                          borderRadius: '50%',
                          zIndex: 2,
                        }}
                      />

                      <iframe
                        ref={iframeRef}
                        title="preview"
                        style={{
                          border: 'none',
                          width: isLandscape ? `${selectedWindowSize.height}px` : `${selectedWindowSize.width}px`,
                          height: isLandscape ? `${selectedWindowSize.width}px` : `${selectedWindowSize.height}px`,
                          background: 'white',
                          display: 'block',
                        }}
                        src={peekUrl ?? iframeUrl}
                        onLoad={handleIframeLoad}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  title="preview"
                  className="border-none w-full h-full bg-page"
                  src={peekUrl ?? iframeUrl}
                  onLoad={handleIframeLoad}
                />
              )}
              {/* Cover the iframe with the loading state whenever there's no REAL
                  page yet — not just mid-build. This hides the server's bare
                  "Start prompting." / "empty" stub in every state (idle, stopped,
                  between builds), so the user always sees the editorial loader
                  instead of a blank stub. previewHasContent flips true only on the
                  router's 'ready' message (a real route rendered). */}
              {(!iframeLoaded || !previewHasContent || previewUnreachable) && (
                <div className="absolute inset-0 z-10">
                  <PreviewLoadingCarousel />
                </div>
              )}
            </>
          ) : (
            <PreviewLoadingCarousel />
          )}

          {isStaticPreview && <StaticPreviewBanner />}

          {peekVersion && (
            <div className="absolute top-0 inset-x-0 z-40 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-3 sm:px-4 py-2 bg-raised/95 backdrop-blur border-b border-hairline text-xs text-secondary">
              <span className="i-ph:clock-counter-clockwise text-accent" />
              <span className="hidden sm:inline">Viewing an older version — read-only</span>
              <span className="sm:hidden">Older version · read-only</span>
              <button
                type="button"
                onClick={() => setConfirmRestoreOpen(true)}
                className="px-2.5 py-1 rounded-md bg-accent text-black font-medium hover:opacity-90 transition-opacity touch:min-h-9"
              >
                <span className="hidden sm:inline">Restore this version</span>
                <span className="sm:hidden">Restore</span>
              </button>
              <button
                type="button"
                onClick={() => exitPeek()}
                className="ui-btn-icon"
                title="Back to current"
                aria-label="Back to current version"
              >
                <span className="i-ph:x text-sm" />
              </button>
            </div>
          )}
          <ConfirmationDialog
            isOpen={confirmRestoreOpen}
            onClose={() => setConfirmRestoreOpen(false)}
            onConfirm={() => {
              setConfirmRestoreOpen(false);

              if (peekVersion) {
                requestRestore(peekVersion);
              }
            }}
            title="Restore this version?"
            description="This restores your project files to this version. Anything built after this point will be lost from the working copy."
            confirmLabel="Restore"
            variant="destructive"
          />

          {isDeviceModeOn && !showDeviceFrameInPreview && (
            <>
              {/* Width indicator */}
              <div
                style={{
                  // Inside this overflow:auto box — a negative top was clipped, so it never showed.
                  position: 'absolute',
                  top: '8px',
                  left: '50%',
                  zIndex: 20,
                  transform: 'translateX(-50%)',
                  background: 'var(--dunea-elements-background-depth-3, rgba(0,0,0,0.7))',
                  color: 'var(--dunea-elements-textPrimary, white)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  opacity: resizingState.current.isResizing ? 1 : 0,
                  transition: 'opacity 0.3s',
                }}
              >
                {currentWidth}px
              </div>

              <ResizeHandle side="left" />
              <ResizeHandle side="right" />
            </>
          )}

          {selectorAvailable && (
            <ElementStylePanel
              onPreview={postStylePreview}
              onReset={postStyleReset}
              onSave={handleStyleSave}
              onReplaceImage={handleReplaceImage}
            />
          )}
        </div>
      </div>
    </div>
  );
});
